import React, { useState, useEffect, useMemo } from "react";
import { signOut } from "firebase/auth";
import { auth, firestore, database } from "../../firebaseConfig";
import { doc, getDoc, collection, query, getDocs, setDoc } from "firebase/firestore";
import { ref, set } from "firebase/database";
import toast from "react-hot-toast";

const EmployeeDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const requestClosedMessage = "This company is not accepting employee requests right now.";
  const selectedCompanyData = useMemo(
    () => companies.find((c) => c.id === selectedCompany),
    [companies, selectedCompany]
  );
  const companyAcceptingRequests = selectedCompanyData?.allowEmployeeRequests !== false;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          window.location.href = "/login";
          return;
        }

        // Fetch user data from Firestore
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }

        // Fetch list of companies
        const companiesSnapshot = await getDocs(collection(firestore, "companies"));
        const companiesList = [];
        companiesSnapshot.forEach((doc) => {
          companiesList.push({ id: doc.id, ...doc.data() });
        });
        setCompanies(companiesList);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleSendRequest = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company");
      return;
    }

    if (!companyAcceptingRequests) {
      toast.error(requestClosedMessage);
      return;
    }

    setSendingRequest(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Send request to company in Realtime Database
      await set(
        ref(database, `Companies/${selectedCompany}/EmployeeRequests/${user.uid}`),
        {
          uid: user.uid,
          fullName: userData.fullName,
          email: userData.email,
          requestedAt: Date.now(),
          status: "pending",
        }
      );

      // Update user's Firestore document
      await setDoc(doc(firestore, "users", user.uid), {
        ...userData,
        requestedCompany: selectedCompany,
        employeeStatus: "pending",
      }, { merge: true });

      toast.success("Request sent successfully!");
      setSelectedCompany("");
      
      // Refresh user data
      const userDoc = await getDoc(doc(firestore, "users", user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error("Error sending request:", error);
      toast.error("Failed to send request");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/login";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <header className="flex justify-between items-center mb-6 bg-white shadow-md rounded-lg p-4">
        <div>
          <h1 className="text-2xl font-bold text-blue-700">Employee Dashboard</h1>
          <p className="text-sm text-gray-600">Welcome, {userData?.fullName}</p>
        </div>
        <button
          onClick={handleLogout}
          className="bg-blue-700 text-white px-4 py-2 rounded hover:bg-blue-800 transition"
        >
          Logout
        </button>
      </header>

      <main className="space-y-6">
        {/* Account Status */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Account Status</h2>
          {userData?.employeeStatus === "suspended" ? (
            <div className="space-y-2">
              <p className="text-amber-600 font-medium flex items-center gap-2">
                🚫 Your account is suspended
              </p>
              <p className="text-gray-600 text-sm">
                You currently cannot access company resources. Please contact your company admin for assistance.
              </p>
            </div>
          ) : userData?.employeeStatus === "removed" ? (
            <div className="space-y-2">
              <p className="text-red-600 font-medium flex items-center gap-2">
                ❌ You are no longer linked to a company
              </p>
              <p className="text-gray-600 text-sm">
                Send a new request below to join a company.
              </p>
            </div>
          ) : userData?.companyName ? (
            <div className="space-y-2">
              <p className="text-green-600 font-medium">
                ✅ Connected to: <span className="font-bold">{userData.companyName}</span>
              </p>
              <p className="text-gray-600">Role: {userData.employeeRole || "Employee"}</p>
            </div>
          ) : userData?.employeeStatus === "pending" && userData?.requestedCompany ? (
            <div className="space-y-2">
              <p className="text-amber-600 font-medium">
                ⏳ Pending approval from: <span className="font-bold">{userData.requestedCompany}</span>
              </p>
              <p className="text-gray-600 text-sm">
                Your request is awaiting admin approval.
              </p>
            </div>
          ) : (
            <p className="text-gray-600">
              No company connection yet. Send a request to join a company below.
            </p>
          )}
        </div>

        {/* Send Company Request */}
        {!userData?.companyName && (
          <div className="bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              Join a Company
            </h2>
            <p className="text-gray-600 mb-4">
              Select a company to send a join request to their admin:
            </p>

            <div className="space-y-4">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyName}
                    {company.allowEmployeeRequests === false ? " (Not accepting requests)" : ""}
                  </option>
                ))}
              </select>

              <button
                onClick={handleSendRequest}
                disabled={sendingRequest || !selectedCompany || !companyAcceptingRequests}
                className={`w-full py-3 rounded-lg font-semibold text-white transition ${
                  sendingRequest || !selectedCompany || !companyAcceptingRequests
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
              {sendingRequest ? "Sending..." : "Send Request"}
            </button>
            {!companyAcceptingRequests && selectedCompany && (
              <p className="text-sm text-red-600">
                {requestClosedMessage}
              </p>
            )}
            </div>
          </div>
        )}

        {/* Information */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            Getting Started
          </h2>
          <ul className="space-y-2 text-gray-600">
            <li>• Send a request to join a company</li>
            <li>• Wait for admin approval</li>
            <li>• Once approved, you'll have access to company tools</li>
            <li>• Your role and permissions will be assigned by the admin</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default EmployeeDashboard;
